import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, catchError, from, map, noop, of, switchMap, take, tap } from 'rxjs';
import { UserDTO } from '@splitory/splitory-dto';
import { splEnvironment, splOriginEnvironment } from '@splitory/splitory-environment';
import { SplHttpBaseService } from '../base';

@Injectable({
  providedIn: 'root',
})
export class SplHttpAuthService extends SplHttpBaseService {
  private readonly router = inject(Router);

  protected override baseEndpoint = 'auth';

  private readonly _user = signal<UserDTO | null>(null);
  readonly user = this._user.asReadonly();
  readonly user$ = toObservable(this._user);

  private readonly _userLoading = signal<boolean>(false);
  readonly userLoading = this._userLoading.asReadonly();
  readonly userLoading$ = toObservable(this._userLoading);

  private readonly _authState = signal(false);
  readonly authState = this._authState.asReadonly();
  readonly authState$ = toObservable(this._authState);

  private readonly _authStateLoading = signal(false);
  readonly authStateLoading = this._authStateLoading.asReadonly();
  readonly authStateLoading$ = toObservable(this._authStateLoading);

  signInWithGoogle(): void {
    window.location.href = `${splEnvironment.origin.api}/${splEnvironment.api.globalPrefix}/${this.baseEndpoint}/google?originAlias=${splOriginEnvironment.originAlias}`;
  }

  loadAuthenticatedUser(): Observable<UserDTO | null> {
    this._authStateLoading.set(true);

    return from(cookieStore.get(splEnvironment.publicCookieNames.authContextPresence)).pipe(
      switchMap((authContextPresent) => {
        if (!authContextPresent) {
          this._authStateLoading.set(false);
          return of(null);
        }

        this._userLoading.set(true);

        return this.get<UserDTO>('user').pipe(
          catchError(() => of({ data: null })),
          map(({ data }) => data),
          tap((userDTO) => {
            this._user.set(userDTO);
            this._userLoading.set(false);

            this._authState.set(true);
            this._authStateLoading.set(false);
          }),
        );
      }),
    );
  }

  signOut(): void {
    this._authStateLoading.set(true);

    this.get('signOut')
      .pipe(
        tap(() => {
          this._authState.set(false);
          this._authStateLoading.set(false);
        }),
        switchMap(() => from(this.router.navigate(['/auth']))),
        tap(() => this._user.set(null)),
        map(noop),
        take(1),
      )
      .subscribe();
  }
}
